import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  mergeMap,
  of,
  switchMap,
  take,
} from 'rxjs';
import { PermissionsService } from '@shared/services/permissions.service';
import { IrisService } from '@shared/services/iris.service';
import { Store } from '@ngrx/store';
import {
  setSiteId,
  setStudyId,
} from '@shared/store/actions/siteWorkspace.action';

import {
  PermissionGrants,
  ReassignSubmission,
  SiteDetail,
  SiteTeam,
} from '@shared/interfaces/permissions.interface';
import { ERMDatePipe } from '@shared/pipes/erm-date.pipe';
import { SiteData, SiteDetails } from '@data-access/sites';

@Injectable()
export class SitesDetailFacadeService {
  private readonly _siteService = inject(IrisService);
  private readonly _permissionsService = inject(PermissionsService);
  private readonly _ermDatePipe = inject(ERMDatePipe);
  private readonly store = inject(Store<any>);

  private siteDetailView$ = new BehaviorSubject<SiteDetails | null>(null);

  get siteDetails$() {
    return this.siteDetailView$.asObservable().pipe(
      switchMap(() =>
        combineLatest([
          this.siteDetailView$.asObservable(),
          this._permissionsService.isInternal$,
        ]).pipe(
          mergeMap(([siteDetails, isInternal]) => {
            if (!siteDetails?.id) {
              return of({
                canEdit: false,
                canRemove: false,
                canReassign: false,
                isInternal,
                siteDetails,
              });
            }

            const canEdit$ = this._permissionsService.observeEntityPermissions(
              siteDetails.id,
              [SiteTeam, SiteDetail],
              [PermissionGrants.CanEdit],
            );

            const canRemove$ =
              this._permissionsService.observeEntityPermissions(
                siteDetails.id,
                [SiteTeam, SiteDetail],
                [PermissionGrants.CanRemove],
              );

            const canReassign$ =
              this._permissionsService.observeUserPermissions(
                [ReassignSubmission],
                [PermissionGrants.CanEdit],
              );

            return combineLatest({
              canEdit: canEdit$,
              canRemove: canRemove$,
              canReassign: canReassign$,
              isInternal: of(isInternal),
              siteDetails: of(siteDetails),
            });
          }),
        ),
      ),
    );
  }

  loadSiteDetailFn(siteId: string) {
    return this._siteService.getSiteDetailsById(siteId);
  }

  loadSiteDetails(siteId: string): void {
    this.loadSiteDetailFn(siteId)
      .pipe(take(1))
      .subscribe((res: SiteDetails) => {
        if (res) {
          const panelId = (res.data as SiteData | null)?.panel_id;
          if (
            res.data &&
            Object.keys(res.data)?.length > 0 &&
            Object.prototype?.hasOwnProperty.call(res.data, 'panel_id') &&
            panelId
          ) {
            res.oversight = [panelId];
          } else {
            if (res.investigator?.contact_address?.country.name) {
              res.oversight = [res.investigator.contact_address.country.name];
            }
          }

          // Set permissions for the entity
          if (res?.role) {
            this._permissionsService.setEntityRolesAndPermission(
              res.id,
              res?.role,
            );
          }

          if (res.status?.toLowerCase() === 'closed') {
            res.status = `${res.status} (${this._ermDatePipe.transform(res.closure_dt) || ''})`;
          }

          // Dispatch site and study IDs to the store
          this.store.dispatch(setStudyId({ study_id: res.study.id }));
          this.store.dispatch(setSiteId({ site_id: res.id }));

          // Update the site detail view model
          this.siteDetailView$.next(res);
        }
      });
  }
}
